import React, { useRef, useEffect, useState } from 'react';
import { TOPICS } from '../../data/mock';
import { Mic, Calendar, MapPin, Users, Lightbulb, ArrowRight } from 'lucide-react';
import { useIsMobile, useIsTablet } from '../../hooks/useWindowSize';

const useInView = (threshold = 0.06) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const TEDxSection = () => {
  const [ref1, inView1] = useInView();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const pad = isMobile ? '0 20px' : isTablet ? '0 32px' : '0 48px';

  return (
    <section id="tedx" ref={ref1} style={{ background: '#0A0A0A', padding: isMobile ? '64px 0' : '100px 0' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: pad }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '36px', height: '3px', background: '#E62B1E' }} />
          <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '3px', color: '#E62B1E', textTransform: 'uppercase' }}>Upcoming</span>
        </div>
        <h2 style={{
          fontFamily: '"Bebas Neue", sans-serif',
          fontSize: isMobile ? '38px' : 'clamp(40px, 5vw, 64px)',
          color: '#FFFFFF', letterSpacing: '2px', margin: '0 0 40px 0', lineHeight: 1
        }}>TEDx <span style={{ color: '#E62B1E' }}>SRMIST</span><br />SPEAKING ENGAGEMENT</h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '1fr 360px',
          gap: isMobile ? '24px' : '48px',
          alignItems: 'start'
        }}>
          {/* Main TEDx card */}
          <div style={{
            background: '#111111', border: '1px solid #1E1E1E',
            borderRadius: '8px', overflow: 'hidden',
            opacity: inView1 ? 1 : 0,
            transform: inView1 ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease'
          }}>
            <div style={{
              background: '#E62B1E',
              padding: isMobile ? '20px 24px' : '24px 32px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <p style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '26px', color: '#fff', letterSpacing: '3px', margin: 0 }}>TEDxSRMIST</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: 'rgba(255,255,255,0.75)', margin: '3px 0 0 0' }}>SRM University, Delhi NCR</p>
              </div>
              <Mic size={26} color="rgba(255,255,255,0.35)" />
            </div>

            <div style={{ padding: isMobile ? '24px' : '32px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                {[
                  { icon: Calendar, text: '24 March 2026' },
                  { icon: MapPin, text: 'SRM University, Delhi NCR, India' },
                  { icon: Users, text: 'Students, Innovators & Aspiring Entrepreneurs' }
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <Icon size={14} color="#E62B1E" style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#CCCCCC' }}>{text}</span>
                  </div>
                ))}
              </div>

              <div style={{
                background: 'rgba(230,43,30,0.07)', border: '1px solid rgba(230,43,30,0.15)',
                borderRadius: '6px', padding: '20px', marginBottom: '16px'
              }}>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#E62B1E', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 8px 0' }}>Topic Theme</p>
                <p style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '20px', color: '#FFFFFF', letterSpacing: '1px', margin: '0 0 8px 0', lineHeight: 1.3 }}>ENTREPRENEURIAL JOURNEY &amp; LEADERSHIP</p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#6B6B6B', margin: 0, lineHeight: 1.65 }}>
                  "We aim to highlight journeys, insights, and perspectives that inspire individuals to think differently and create meaningful impact."
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#161616', borderRadius: '6px', padding: '14px 16px' }}>
                <Lightbulb size={14} color="#E62B1E" style={{ marginTop: '2px', flexShrink: 0 }} />
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#6B6B6B', margin: 0, lineHeight: 1.6 }}>
                  The keynote will draw from entrepreneurial journey, AI-first company building, and India's technological rise.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{
            opacity: inView1 ? 1 : 0,
            transform: inView1 ? 'translateX(0)' : 'translateX(20px)',
            transition: 'opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s'
          }}>
            <h3 style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '18px', color: '#FFFFFF', letterSpacing: '2px', margin: '0 0 18px 0' }}>EXPERTISE AREAS</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '28px' }}>
              {TOPICS.map((topic, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '11px 14px',
                  background: '#0E0E0E', border: '1px solid #1A1A1A', borderRadius: '6px',
                  opacity: inView1 ? 1 : 0,
                  transition: `opacity 0.5s ease ${0.3 + i * 0.05}s`
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(230,43,30,0.3)'; e.currentTarget.style.background = '#141414'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#1A1A1A'; e.currentTarget.style.background = '#0E0E0E'; }}
                >
                  <ArrowRight size={11} color="#E62B1E" style={{ flexShrink: 0 }} />
                  <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#AAAAAA' }}>{topic}</span>
                </div>
              ))}
            </div>

            <div style={{ background: '#111111', border: '1px solid #1E1E1E', borderRadius: '8px', padding: '24px', borderLeft: '3px solid #E62B1E' }}>
              <p style={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '36px', color: '#E62B1E', margin: '0 0 2px 0', letterSpacing: '2px' }}>420+</p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', color: '#CCCCCC', fontWeight: 600, margin: '0 0 8px 0' }}>Public Posts</p>
              <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', color: '#5A5A5A', margin: 0, lineHeight: 1.6 }}>Content reaching hundreds of thousands weekly - sparking discussion among founders, operators, and enterprise decision-makers.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TEDxSection;
